import { FC, MouseEvent } from 'react';

import { setHoveredId } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDialog } from 'hooks/useDialog';

import { RaaSpinner } from 'raa-react';

import { selectedLmByggnadsPolygonLayer } from 'components/map/Layers';
import { lmCircleSvg } from 'components/map/icons';
import CreateUtkastDialog from 'components/utkast/CreateUtkastDialog/CreateUtkastDialog';

import { ByggnadIndex, RequestStatus, RequestType } from 'models';

import classNames from 'classnames';
import { getAndamalString } from 'utilities/getAndamalString';

export const LmPopupRow: FC<{
  byggnad: ByggnadIndex;
  requestType: RequestType;
  utkastRequestStatus: RequestStatus;
  createUtkast: (lmRegisterbyggnadId: string, aktivitetId: string) => Promise<void>;
  aktivitetId: string;
}> = ({ byggnad: { lmbyggnad, raabyggnad }, requestType, utkastRequestStatus, createUtkast, aktivitetId }) => {
  const hoveredId = useAppSelector((state: RootState) => state.map.hoveredId);
  const bebyggelseobjektUtkast = useAppSelector((state: RootState) => state.bebyggelseobjektUtkast.data);
  const dispatch = useAppDispatch();
  const { setDialog, unsetDialog } = useDialog();

  const handleHover = (event: MouseEvent<HTMLButtonElement>, id: string) => {
    event.preventDefault();
    dispatch(setHoveredId(id));
    selectedLmByggnadsPolygonLayer.changed();
  };

  const hasPreExistingUtkastWithId = !!bebyggelseobjektUtkast?.some(
    (u) => u.lm_registerbyggnad?.objektidentitet === lmbyggnad.objektidentitet
  );

  return (
    <div
      className={classNames(
        'tw-flex tw-flex-col tw-items-center tw-gap-3 tw-border-b tw-border-raa-gray-3 tw-p-4 md:tw-flex-row',
        {
          'tw-bg-raa-gray-2': lmbyggnad.objektidentitet === hoveredId
        }
      )}
    >
      <div className="tw-flex tw-gap-3">
        <img
          className="tw-w-10"
          src={`data:image/svg+xml;utf8,${encodeURIComponent(lmCircleSvg())}`}
          alt={`Ikon för byggnad från Lantmäteriet`}
        />

        <div className="tw-flex tw-flex-col">
          <p className="tw-font-semibold">{getAndamalString(lmbyggnad.byggnadsandamal) || 'Ändamål saknas!'}</p>
          <p className="tw-text-size-0.813 tw-capitalize">
            {lmbyggnad.beteckning.toLowerCase()}, hus {lmbyggnad.husnummer}
          </p>
          {(raabyggnad.existerar || hasPreExistingUtkastWithId) && (
            <span className="tw-flex tw-items-center tw-gap-1 tw-text-size-0.813">
              <span className="raa-icon-error tw-text-raa-orange" />
              {getVarningstexter(raabyggnad.existerar, hasPreExistingUtkastWithId)}
            </span>
          )}
        </div>
      </div>
      <button
        onMouseEnter={(event) => handleHover(event, lmbyggnad.objektidentitet)}
        onMouseLeave={(event) => handleHover(event, '')}
        className="raa-button raa-button-secondary tw-ml-auto tw-w-full md:tw-w-[unset] md:tw-min-w-[8rem]"
        aria-label={`Skapa utkast för byggnad ${lmbyggnad.husnummer}`}
        disabled={(requestType === 'post' || requestType === 'patch') && utkastRequestStatus === 'pending'}
        onClick={() => {
          if (raabyggnad.existerar || hasPreExistingUtkastWithId) {
            setDialog(CreateUtkastDialog, {
              createUtkast,
              lmRegisterbyggnadId: lmbyggnad.objektidentitet,
              aktivitetId,
              unsetDialog
            });
          } else {
            void createUtkast(lmbyggnad.objektidentitet, aktivitetId);
          }
        }}
      >
        {((requestType === 'post' || requestType === 'patch') && utkastRequestStatus === 'pending' && (
          <RaaSpinner size={'1rem'} />
        )) ||
          (raabyggnad.existerar || hasPreExistingUtkastWithId ? 'skapa' : 'lägg till')}
      </button>
    </div>
  );
};

const getVarningstexter = (finnsIBebyggelseregistret: boolean, harExisterandeUtkast: boolean): string =>
  [finnsIBebyggelseregistret && 'Finns i bebyggelseregistret', harExisterandeUtkast && 'Har aktivt utkast']
    .filter((t) => !!t)
    .join(', ');
